/**
 * One feature → one small map image, for the Report tool.
 *
 * `mapCapture.ts`'s `captureMapOL` photographs whatever a REAL, mounted OL
 * viewport is currently showing — it takes no center/zoom, so it cannot be
 * pointed at an arbitrary feature directly. This builds a small, OFFSCREEN
 * OL map (own basemap + a one-feature vector layer, styled through the
 * exact same `styleFunctionFor` the real map uses), zooms it to that one
 * feature, captures it with the existing utility, draws the layer's label
 * on top if labeling is on, then tears the whole thing down — one feature
 * rendered and discarded before the next starts, so report generation has
 * a bounded, predictable memory/network footprint regardless of how many
 * features it is asked for.
 */

import Map from 'ol/Map';
import View from 'ol/View';
import TileLayer from 'ol/layer/Tile';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import XYZ from 'ol/source/XYZ';
import GeoJSON from 'ol/format/GeoJSON';
import { fromLonLat, toLonLat } from 'ol/proj';
import { getCenter, type Extent } from 'ol/extent';

import { BASEMAPS, styleFunctionFor } from '../MapViewOL';
import { captureMapOL, type MapCapture } from './mapCapture';
import { anchorsForGeometry, type Project } from '../labeling/LabelUtils';
import { resolveLabelText, passesLabelFilter } from '../labeling/LabelExpression';
import { drawLabels, type LabelDrawItem } from '../labeling/LabelRenderer';
import { labelingIsActive } from '../labeling/LabelTypes';
import { isWithinScale, scaleAt } from '../labeling/ScaleVisibility';
import { DEFAULT_RENDERER } from '../layers/symbols';
import type { Layer, BasemapId } from '../types';

/** Waits for the offscreen map to finish painting (tiles loaded), with a
 *  timeout so one unreachable basemap/feature can never hang the whole
 *  report. */
function waitForRender(map: Map, timeoutMs = 4000): Promise<void> {
  return new Promise((resolve) => {
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      resolve();
    };
    map.once('rendercomplete', finish);
    map.render();
    setTimeout(finish, timeoutMs);
  });
}

function isPointGeometry(geometry: GeoJSON.Geometry | null | undefined): boolean {
  return geometry?.type === 'Point' || geometry?.type === 'MultiPoint';
}

export async function captureFeatureThumbnail(
  feature: GeoJSON.Feature,
  layer: Layer,
  basemap: BasemapId,
  width = 240,
  height = 160,
): Promise<MapCapture | null> {
  if (!feature.geometry) return null;

  const container = document.createElement('div');
  // Off the visible page, not display:none — OL needs real layout to size
  // its canvases, which a display:none/zero-size container never gets.
  container.style.position = 'fixed';
  container.style.left = '-99999px';
  container.style.top = '0';
  container.style.width = `${width}px`;
  container.style.height = `${height}px`;
  document.body.appendChild(container);

  let map: Map | null = null;
  try {
    const basemapEntry = BASEMAPS[basemap] ?? BASEMAPS.street;
    const basemapLayer = new TileLayer({
      source: new XYZ({
        url: basemapEntry.url,
        attributions: basemapEntry.attribution,
        maxZoom: basemapEntry.maxNativeZoom,
        // Matches MapViewOL's own basemap source — without this the
        // capture step below would find the basemap layer's canvas
        // tainted and silently skip it.
        crossOrigin: 'anonymous',
      }),
    });

    // `readFeatures` (plural) always returns an array, unlike `readFeature`
    // which is typed as one-or-many depending on the GeoJSON object handed
    // in — simpler to type correctly for a single, known-Feature input.
    const olFeatures = new GeoJSON().readFeatures(feature, {
      dataProjection: 'EPSG:4326',
      featureProjection: 'EPSG:3857',
    });
    const source = new VectorSource({ features: olFeatures });
    const renderer = layer.renderer ?? DEFAULT_RENDERER;
    const vectorLayer = new VectorLayer({ source, style: styleFunctionFor(renderer, layer.opacity ?? 1) });

    const view = new View({ center: [0, 0], zoom: 2 });
    map = new Map({
      target: container,
      layers: [basemapLayer, vectorLayer],
      view,
      controls: [],
      interactions: [],
    });

    const extent = source.getExtent();
    const hasExtent = extent.every((value) => Number.isFinite(value));
    if (hasExtent) {
      if (isPointGeometry(feature.geometry)) {
        // Fitting a point-only extent degenerates to zero size — a fixed,
        // street-level zoom reads far better than OL's own fallback.
        view.setCenter(getCenter(extent));
        view.setZoom(17);
      } else {
        view.fit(extent as Extent, { size: [width, height], padding: [20, 20, 20, 20], maxZoom: 19 });
      }
    }

    await waitForRender(map);
    const capture = await captureMapOL(map.getViewport(), 2);

    if (labelingIsActive(layer.labeling)) {
      drawFeatureLabel(capture.canvas, feature, layer, map, width, height, 2);
    }

    return capture;
  } catch (error) {
    console.warn(`[report] thumbnail failed for a feature on "${layer.name}"`, error);
    return null;
  } finally {
    map?.setTarget(undefined);
    container.remove();
  }
}

/** Draws this one feature's label onto an already-captured thumbnail
 *  canvas — the SAME loop `MapViewOL.tsx`'s own label-overlay effect runs
 *  per frame, just against one feature instead of a whole layer's worth. */
function drawFeatureLabel(
  canvas: HTMLCanvasElement,
  feature: GeoJSON.Feature,
  layer: Layer,
  map: Map,
  width: number,
  height: number,
  scale: number,
): void {
  const context = canvas.getContext('2d');
  if (!context || !layer.labeling) return;

  const view = map.getView();
  const [, centerLat] = toLonLat(view.getCenter() ?? [0, 0]);
  const mapScale = scaleAt(centerLat, view.getZoom() ?? 0);

  const project: Project = (lon, lat) => {
    const pixel = map.getPixelFromCoordinate(fromLonLat([lon, lat]));
    return pixel ? { x: pixel[0], y: pixel[1] } : { x: -9999, y: -9999 };
  };

  const properties = (feature.properties ?? {}) as Record<string, unknown>;
  const items: LabelDrawItem[] = [];
  for (const labelClass of layer.labeling.classes) {
    if (!labelClass.field.trim() && !labelClass.expression.trim()) continue;
    if (!isWithinScale(mapScale, labelClass.scale)) continue;
    if (!passesLabelFilter(labelClass.filter, properties)) continue;

    const text = resolveLabelText(labelClass, properties);
    if (!text) continue;

    const anchors = anchorsForGeometry(feature.geometry, project, labelClass.placement.repeat);
    for (const anchor of anchors) items.push({ text, anchor, labelClass });
  }

  if (items.length === 0) return;
  context.save();
  context.scale(scale, scale);
  drawLabels(context, items, { width, height });
  context.restore();
}
